import React from 'react';
import { EntityListPanel } from '../../../../shared/components/EntityListPanel';
import { triggerLabel } from '../healthUtils';
import type { ReconcilerExecution, ReconcilerExecutionStatus } from '../../../../types/frontend';
import type { ReconcilerHealth } from '../../../../types/backend';

export interface ReconcilerSummary {
  name: string;
  domain_label: string;
  latestStatus: ReconcilerExecutionStatus;
  executions: ReconcilerExecution[];
}

export interface ReconcilerListItem {
  name: string;
  health?: ReconcilerHealth;
}

const LIST_CONFIG = {
  getPrimaryText: (r: ReconcilerListItem) => r.name,
  getSecondaryText: (r: ReconcilerListItem) =>
    r.health ? triggerLabel(r.health.schedule) : '',
  getId: (r: ReconcilerListItem) => r.name,
};

export function groupByReconciler(executions: ReconcilerExecution[]): ReconcilerSummary[] {
  const ATTENTION_ORDER: Record<string, number> = {
    FAILED: 0, DRIFTED: 1, IN_PROGRESS: 2, SETTLING: 3, PENDING: 4, CONVERGED: 5, IDLE: 6,
  };

  function worstStatus(execs: ReconcilerExecution[]): ReconcilerExecutionStatus {
    if (execs.length === 0) return 'IDLE';
    let worst: ReconcilerExecutionStatus = execs[0].status;
    let worstRank = ATTENTION_ORDER[worst] ?? 7;
    for (const e of execs) {
      const rank = ATTENTION_ORDER[e.status] ?? 7;
      if (rank < worstRank) {
        worst = e.status;
        worstRank = rank;
      }
    }
    return worst;
  }

  const map = new Map<string, ReconcilerExecution[]>();
  for (const e of executions) {
    const list = map.get(e.reconciler);
    if (list) list.push(e);
    else map.set(e.reconciler, [e]);
  }

  const summaries: ReconcilerSummary[] = [];
  for (const [name, execs] of map) {
    const sorted = execs.sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime());
    summaries.push({
      name,
      domain_label: sorted[0].domain_label,
      latestStatus: worstStatus(sorted),
      executions: sorted,
    });
  }

  return summaries.sort((a, b) => (ATTENTION_ORDER[a.latestStatus] ?? 6) - (ATTENTION_ORDER[b.latestStatus] ?? 6));
}

export interface ReconcilerListPanelProps {
  items: ReconcilerListItem[];
  selectedName: string | null;
  onSelect: (name: string) => void;
}

const ReconcilerListPanel: React.FC<ReconcilerListPanelProps> = ({
  items,
  selectedName,
  onSelect,
}) => {
  const selected = items.find((r) => r.name === selectedName) ?? null;

  return (
    <EntityListPanel
      entities={items}
      selectedEntity={selected}
      isNew={false}
      saving={false}
      config={LIST_CONFIG}
      emptyStateText="No reconcilers found"
      emptyStateHelperText="No reconciler domains registered"
      onSelect={(r) => onSelect(r.name)}
    />
  );
};

export default ReconcilerListPanel;
